/**
 * 69.29 step 1 (ARCHITECTURE §5.25 (f), DECISIONS 245): the tank's look — the tracked carrier's D look (vehicleLooks
 * `trackedD`, the "tank like body"), its mortar-like tube swapped for a direct-fire gun. The owner: *"We also have the tank like
 * body model so I think we can use that for tank I just want to swap the gun out with something else as it's a mortar"*; his
 * picks (245): *"1: 2 rounds 2. machine gun coax but next to main gun from turret 3. cna add now 4. not sure the question 5.
 * sure 6. for sure its own look"*.
 *
 * What it builds on the D body: the gun (a longer, straighter, slimmer tube on the mantlet; it recoils in its cradle, which
 * pitches −0.15 to +0.35 with the lay, the turret turning with `turretYaw`), the coaxial gun's port and barrel beside it in
 * the mantlet, the commander's pintle gun (on the cupola, or on the D's hatch), the two rounds (an explosive round and a
 * solid one, as cartridges and in flight), the damaged forms mobility kills want (a thrown track and the road wheels knocked
 * off; the engine deck blown and smoking), and the fit lane F's sim takes (the hull, the turret its own box, the running gear,
 * the engine).
 *
 * The choices (each with a lean on the sheet, `design_sheets/tank.html`):
 *   - `TankEra`: A modern — a smoothbore with a fume extractor and a thermal sleeve's bands; the explosive round and a dart
 *     (fin-stabilised, its sabot's petals falling away); B older — a rifled gun with the D's muzzle brake; the explosive
 *     round and a full-bore solid shot.
 *   - `TankGunLength`: A 2.4 people from the mantlet (about four fifths of the hull: a battle tank's proportion), B 3.0 (the
 *     note's long end: as long as the hull).
 *   - `TankTurret`: A the D's turret as it is, B with a bustle at its rear (a stowage box and its rack): a longer turret,
 *     read as a tank's from the diorama.
 *   - `TankCommander`: A the commander's gun on the cupola (the commander up in it, the vision blocks theirs), B on the D's
 *     right hatch as today.
 * Model units (a person is 1.8), as the vehicle models. Presentation only.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { HitPart } from '../types/body';
import { buildVehicleLook } from './vehicleLooks';
import { updateVehicleModel, type VehicleModel } from './vehicleModels';
import { fitSmokeLaunchers } from './smokeLauncher';
import { SMOKE_LAUNCHER_FITS } from '../constants';

/**
 * The owner's picks (DECISIONS 247, Oct 23): *"1. first option is good, 2. 2.4, 3. turret bustle, 4. cupola"* — the modern
 * gun and its dart, the gun 2.4 people from the mantlet, the turret with its bustle, the commander's gun on the cupola. What
 * play builds.
 */
export const TANK_PICKS = { era: 'A', length: 'A', turret: 'B', commander: 'A' } as const;

export type TankEra = 'A' | 'B';
export type TankGunLength = 'A' | 'B';
export type TankTurret = 'A' | 'B';
export type TankCommander = 'A' | 'B';
export type TankRound = 'he' | 'solid';
export const TANK_ERAS: Record<TankEra, string> = {
  A: 'modern: a smoothbore with a fume extractor and a thermal sleeve\'s bands; the explosive round and a dart (fin-stabilised, its sabot\'s petals falling away at the muzzle)',
  B: 'older: a rifled gun with the D\'s muzzle brake; the explosive round and a full-bore solid shot',
};
export const TANK_GUN_LENGTHS: Record<TankGunLength, { people: number; label: string }> = {
  A: { people: 2.4, label: '2.4 people from the mantlet: about four fifths of the hull, a battle tank\'s proportion' },
  B: { people: 3.0, label: '3.0 people: the note\'s long end, as long as the hull' },
};
export const TANK_TURRETS: Record<TankTurret, string> = {
  A: 'the D\'s turret as it is',
  B: 'with a bustle at its rear (a stowage box and its rack): a longer turret, read as a tank\'s from the diorama',
};
export const TANK_COMMANDERS: Record<TankCommander, string> = {
  A: 'the commander\'s gun on the cupola: the commander up in it, the vision blocks theirs',
  B: 'on the D\'s right hatch, as today (the cupola then unmanned)',
};
/** The gun's lay (ARCHITECTURE §5.25 (a)): elevation −0.15 to +0.35 rad; the recoil's travel and time; a person in model units. */
export const TANK_GUN = { elevation: [-0.15, 0.35] as const, recoil: { travel: 0.35, seconds: 0.45 }, person: 1.8, mantlet: 0.38 };
/**
 * 69.31: the bustle's ammunition door (with `ammoDoor`, turret B): its hinge's height and depth in the turret's frame (the
 * bustle's back is at z −1.47), its size, and its open turn about x (down and out: a tray).
 */
export const TANK_AMMO_DOOR = { hinge: [0.12, -1.51] as const, width: 1.1, height: 0.34, open: -1.35 };
/**
 * 249: the cupola's gun (commander A) on its ring — the ring turns all the way round (the commander turns with it, the post on
 * its rim), the gun on the post elevates within `elevation` (rad). The ring's centre the cupola's, in the turret's frame.
 */
export const TANK_CUPOLA = { centre: [-0.45, -0.35] as const, post: [0.21, 0.23] as const, elevation: [-0.25, 1.0] as const };

export interface TankModel {
  m: VehicleModel;
  era: TankEra; length: TankGunLength; turretKind: TankTurret; commanderKind: TankCommander;
  /** recoils along the cradle's −z */
  barrel: THREE.Group;
  muzzle: THREE.Object3D;
  /** the coaxial gun's muzzle (in the mantlet, laid with the gun) */
  coaxMuzzle: THREE.Object3D;
  /** the commander's gun: its muzzle, the commander's feet and hands; 249: on the cupola (A) its ring (turns about y) and its cradle (pitches about x) */
  pintle: { muzzle: THREE.Object3D; seat: THREE.Object3D; hands: { right: THREE.Object3D; left: THREE.Object3D }; ring?: THREE.Group; cradle?: THREE.Group };
  /** where a damaged engine deck smokes from (empty until `damageTank`) */
  smoke: THREE.Object3D[];
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color)); m.position.set(x, y, z); parent.add(m); return m;
}
function cyl(parent: THREE.Object3D, r0: number, r1: number, len: number, color: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 12): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r0, r1, len, seg), mat(color));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z); parent.add(m); return m;
}
const anchor = (parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D => { const o = new THREE.Object3D(); o.position.set(x, y, z); parent.add(o); return o; };
const group = (parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Group => { const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g; };

/** A pintle machine gun facing +z on `parent` at its pivot: the receiver, the barrel (thin), the ammunition box; its grips and muzzle. */
function pintleGun(parent: THREE.Object3D, p: UnitPalette): { muzzle: THREE.Object3D; hands: { right: THREE.Object3D; left: THREE.Object3D } } {
  const g = group(parent);
  box(g, 0.1, 0.11, 0.42, p.metal, 0, 0, 0.04);
  cyl(g, 0.022, 0.022, 0.55, p.metal, 0, 0.01, 0.5, 'z', 6).userData.thin = true;
  box(g, 0.14, 0.12, 0.14, p.dark, -0.11, -0.02, 0.06);
  box(g, 0.14, 0.03, 0.03, p.dark, 0, -0.03, -0.2);   // the spade grips' bar
  return { muzzle: anchor(g, 0, 0.01, 0.79), hands: { right: anchor(g, 0.07, -0.03, -0.22), left: anchor(g, -0.07, -0.03, -0.22) } };
}

/**
 * The tank: the D's tracked body (`buildVehicleLook('tracked')`), its gun swapped (the era's and the length's), the coax in
 * the mantlet, the commander's gun where picked, the bustle on turret B, the far tier's gun laid flat. Built at `personHeight`
 * (model units a person is 1.8); not baked (the host bakes it as any vehicle: the barrel a moving part).
 */
export function buildTank(p: UnitPalette, personHeight: number, o: { era?: TankEra; length?: TankGunLength; turret?: TankTurret; commander?: TankCommander; smoke?: boolean; ammoDoor?: boolean } = {}): TankModel {
  const era = o.era ?? 'A', length = o.length ?? 'A', turretKind = o.turret ?? 'B', commanderKind = o.commander ?? 'A';
  const m = buildVehicleLook('tracked', p, personHeight, 'gas');
  const turret = m.turret!, cradle = m.cradle!;
  // the gun: the D's tube, mantlet and brake out; the cradle's block and mantlet again, the barrel (it recoils) on them
  for (const c of [...cradle.children]) cradle.remove(c);
  box(cradle, 0.5, 0.42, 0.5, p.metal, 0, 0, 0);
  const mantlet = new THREE.Mesh(new THREE.ExtrudeGeometry(new THREE.Shape([[-0.34, -0.24], [0.34, -0.24], [0.34, 0.18], [0.22, 0.25], [-0.22, 0.25], [-0.34, 0.18]].map(([x, y]) => new THREE.Vector2(x, y))), { depth: 0.16, bevelEnabled: false }), mat(p.body));
  mantlet.position.z = 0.22; cradle.add(mantlet);
  const L = TANK_GUN_LENGTHS[length].people * TANK_GUN.person, z0 = TANK_GUN.mantlet;
  const barrel = group(cradle); barrel.name = 'barrel';
  cyl(barrel, 0.15, 0.17, 0.3, p.metal, 0, 0, z0 + 0.08, 'z');   // the gun's collar at the mantlet
  if (era === 'A') {
    cyl(barrel, 0.075, 0.085, L, p.metal, 0, 0, z0 + L / 2, 'z').userData.thin = true;
    const fx = z0 + L * 0.42;
    cyl(barrel, 0.12, 0.13, 0.5, p.metal, 0, 0, fx, 'z').userData.thin = true;            // the fume extractor
    for (const t of [0.6, 0.75, 0.9]) cyl(barrel, 0.09, 0.09, 0.06, p.dark, 0, 0, z0 + L * t, 'z').userData.thin = true;   // the thermal sleeve's bands
    box(barrel, 0.06, 0.05, 0.08, p.dark, 0, 0.1, z0 + L - 0.06).userData.thin = true;    // the muzzle's reference sensor
  } else {
    cyl(barrel, 0.08, 0.1, L - 0.3, p.metal, 0, 0, z0 + (L - 0.3) / 2, 'z').userData.thin = true;
    cyl(barrel, 0.12, 0.12, 0.32, p.metal, 0, 0, z0 + L * 0.7, 'z').userData.thin = true;   // the fume extractor, forward
    const brake = group(barrel, 0, 0, z0 + L - 0.15);   // the muzzle brake: a block with its ports, the bore open
    box(brake, 0.26, 0.2, 0.3, p.metal, 0, 0, 0).userData.thin = true;
    for (const s of [-1, 1]) box(brake, 0.02, 0.12, 0.08, '#141414', s * 0.13, 0, 0.04).userData.thin = true;
  }
  const muzzle = anchor(barrel, 0, 0, z0 + L + 0.01);
  // the coaxial gun beside the main gun (to its right, laid with it): its port in the mantlet, its short barrel
  box(cradle, 0.16, 0.15, 0.12, p.metal, 0.24, 0.03, z0 - 0.04);                                         // its armoured housing on the mantlet
  box(cradle, 0.09, 0.09, 0.02, '#141414', 0.24, 0.03, z0 + 0.025);
  cyl(cradle, 0.024, 0.024, 0.22, p.metal, 0.24, 0.03, z0 + 0.1, 'z', 6).userData.thin = true;
  const coaxMuzzle = anchor(cradle, 0.24, 0.03, z0 + 0.22);
  // the commander's gun: A on the cupola (the D's pintle off the right hatch), B as the D has it
  let pintle: TankModel['pintle'];
  if (commanderKind === 'A') {
    const old = m.hands.gunner?.right.parent;
    if (old) old.parent?.remove(old);
    for (const c of [...turret.children]) { const mesh = c as THREE.Mesh; if (mesh.isMesh && Math.abs(c.position.x - 0.45) < 1e-6 && Math.abs(c.position.y - 0.7) < 1e-6 && Math.abs(c.position.z + 0.12) < 1e-6) turret.remove(c); }   // (its post)
    // the turret gunner rides inside, its hatch shut (249: "we don't need the gunner of the tank to be sticking out"): no hands
    delete m.hands.gunner;
    // 249: the ring turns (the commander with it: their place on it), the gun pitches on the post at its rim — the host lays it to the commander's aim
    const [cx, cz] = TANK_CUPOLA.centre, [px, pz] = TANK_CUPOLA.post, ring = group(turret, cx, 0, cz); ring.name = 'cupola ring';
    const was = m.crew.spotter!; was.parent?.remove(was);
    m.crew.spotter = anchor(ring, was.position.x - cx, was.position.y, was.position.z - cz);
    box(ring, 0.06, 0.2, 0.06, p.metal, px, 0.88, pz);                                                     // the post on the cupola's ring
    const cradle = group(ring, px, 1.02, pz); cradle.name = 'cupola gun';
    const g = pintleGun(cradle, p);
    m.hands.spotter = g.hands;
    pintle = { muzzle: g.muzzle, seat: m.crew.spotter, hands: g.hands, ring, cradle };
  } else {
    const h = m.hands.gunner!;
    pintle = { muzzle: anchor(h.right.parent!, 0, 0.01, 0.75), seat: m.crew.gunner!, hands: h };
  }
  // the bustle (turret B): a stowage box at the turret's rear and its rack
  if (turretKind === 'B') {
    box(turret, 1.5, 0.4, 0.5, p.body, 0, 0.3, -1.22);
    box(turret, 1.56, 0.04, 0.56, p.flash, 0, 0.52, -1.22);
    for (const x of [-0.7, -0.35, 0, 0.35, 0.7]) box(turret, 0.03, 0.22, 0.03, p.metal, x, 0.64, -1.5).userData.thin = true;   // the rack's uprights
    box(turret, 1.44, 0.03, 0.03, p.metal, 0, 0.75, -1.5).userData.thin = true;
  }
  // 69.31 (DECISIONS 269 (4), the owner: "Tank should get an ammo door"): the bustle's ammunition door, where the dump's arm
  // loads — hinged at its foot across the bustle's back, opening down and out into a tray (TANK_AMMO_DOOR.open); behind it a
  // dark well and the rack's rounds, seen while it is open. Its own group (it turns); the hit shape unchanged.
  let door: THREE.Group | undefined, loadPoint: THREE.Object3D | undefined;
  if (o.ammoDoor && turretKind === 'B') {
    const D = TANK_AMMO_DOOR;
    box(turret, D.width - 0.05, D.height - 0.04, 0.02, '#15171a', 0, D.hinge[0] + D.height / 2, -1.475);   // the well
    for (let i = 0; i < 6; i++) cyl(turret, 0.055, 0.055, 0.02, '#c8a050', -0.4 + i * 0.16, D.hinge[0] + D.height / 2, -1.48, 'z', 8);   // the rounds' bases in the rack
    door = group(turret, 0, D.hinge[0], D.hinge[1]); door.name = 'ammo door';
    box(door, D.width, D.height, 0.04, p.body, 0, D.height / 2, 0);
    box(door, D.width + 0.02, 0.04, 0.05, p.flash, 0, D.height - 0.02, 0);   // its top edge, the team's: open, it reads as a tray's lip
    for (const x of [-0.35, 0.35]) box(door, 0.12, 0.03, 0.05, p.metal, x, 0.02, 0);   // the hinges
    loadPoint = anchor(turret, 0, D.hinge[0] + 0.06, D.hinge[1] - 0.04 - D.height / 2);   // (a crate set on the open door, its middle)
  }
  // the far tier: the D's mortar tube (pitched) off, the gun laid flat
  for (const c of [...m.silhouette.children]) if (Math.abs(c.rotation.x + 0.9) < 1e-6) m.silhouette.remove(c);
  box(m.silhouette, 0.22, 0.22, L, p.metal, 0, 2.04, -0.35 + 0.62 + z0 + L / 2);
  if (turretKind === 'B') box(m.silhouette, 1.5, 0.4, 0.5, p.body, 0, 1.72, -1.57);
  // the smoke launchers on the turret's flanks (249: "the tank smoke launchers float in the air" — the carrier's places): they turn with it
  if (o.smoke) fitSmokeLaunchers(m, SMOKE_LAUNCHER_FITS.tank, p, turret);
  updateVehicleModel(m, { speed: 0, aimPitch: 0 }, 0);
  m.muzzle = coaxMuzzle;   // (the turret seat's bullet gun is the coax: the D's muzzle went with its tube)
  const smoke: THREE.Object3D[] = [];
  m.tank = { barrel, muzzle, coaxMuzzle, commander: { seat: pintle.seat, hands: pintle.hands, muzzle: pintle.muzzle, ...(pintle.ring && pintle.cradle ? { ring: pintle.ring, cradle: pintle.cradle } : {}) }, smoke, ...(door && loadPoint ? { door, loadPoint } : {}) };   // (play: the barrel recoils on its own; the commander's place and gun)
  return { m, era, length, turretKind, commanderKind, barrel, muzzle, coaxMuzzle, pintle, smoke };
}

/** The barrel's recoil `since` seconds after a shot (absent: home): back fast, home slower (the recuperator). */
export function recoilBarrel(barrel: THREE.Object3D, since?: number): void {
  const k = since === undefined ? 1 : since / TANK_GUN.recoil.seconds;
  barrel.position.z = k >= 1 || k < 0 ? 0 : -TANK_GUN.recoil.travel * (k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85);
}

/** Lay and recoil: the turret's yaw, the gun's elevation (clamped to the lay), `recoil` seconds since a shot (absent: home). */
export function poseTank(t: TankModel, st: { yaw: number; pitch: number; recoil?: number }): void {
  t.m.turret!.rotation.y = st.yaw;
  t.m.cradle!.rotation.x = -Math.max(TANK_GUN.elevation[0], Math.min(TANK_GUN.elevation[1], st.pitch));
  recoilBarrel(t.barrel, st.recoil);
}

/**
 * 249: the cupola's gun laid along `dir` (a direction in the turret's frame: the commander's aim) — the ring turned to its
 * bearing, the gun to its elevation within TANK_CUPOLA's; absent, at rest (ahead, level). No-op without a ring (commander B).
 */
export function poseCupola(commander: { ring?: THREE.Group; cradle?: THREE.Group }, dir?: { x: number; y: number; z: number }): void {
  if (!commander.ring || !commander.cradle) return;
  const yaw = dir ? Math.atan2(dir.x, dir.z) : 0, pitch = dir ? Math.atan2(dir.y, Math.hypot(dir.x, dir.z)) : 0;
  commander.ring.rotation.y = yaw;
  commander.cradle.rotation.x = -Math.max(TANK_CUPOLA.elevation[0], Math.min(TANK_CUPOLA.elevation[1], pitch));
}

// ---- the rounds ----------------------------------------------------------------------------------------------------------

function coloured(g: THREE.BufferGeometry, colour: string): THREE.BufferGeometry {
  const n = g.index ? g.toNonIndexed() : g, c = new THREE.Color(colour), cols = new Float32Array(n.getAttribute('position').count * 3);
  for (let i = 0; i < cols.length; i += 3) { cols[i] = c.r; cols[i + 1] = c.g; cols[i + 2] = c.b; }
  n.setAttribute('color', new THREE.BufferAttribute(cols, 3)); n.deleteAttribute('uv');
  return n;
}
const Z = (g: THREE.BufferGeometry) => g.rotateX(Math.PI / 2);
let roundMat: THREE.MeshLambertMaterial | null = null;
const ROUND_MAT = () => (roundMat ??= markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
/**
 * A round (its nose along +z, its base at z 0): `cartridge` the whole round as loaded (the case and the projectile),
 * `flight` the projectile as it flies (the dart's petals not drawn: `sabot` gives them, falling away). One mesh, one draw
 * (vertex colours); the flight's tracer a small glowing disc at its base (its own material).
 */
export function buildTankRound(kind: TankRound, era: TankEra, form: 'cartridge' | 'flight'): THREE.Group {
  const g = new THREE.Group(), parts: THREE.BufferGeometry[] = [], r = 0.06;
  const caseLen = form === 'cartridge' ? (era === 'A' ? 0.48 : 0.62) : 0;
  if (form === 'cartridge') {
    if (era === 'A') {   // the combustible case on its metal stub base
      parts.push(coloured(Z(new THREE.CylinderGeometry(r + 0.008, r + 0.008, caseLen - 0.05, 12)).translate(0, 0, 0.05 + (caseLen - 0.05) / 2), '#b9a97e'));
      parts.push(coloured(Z(new THREE.CylinderGeometry(r + 0.012, r + 0.012, 0.05, 12)).translate(0, 0, 0.025), '#8a8f92'));
    } else {             // the brass case, its rim
      parts.push(coloured(Z(new THREE.CylinderGeometry(r + 0.004, r + 0.012, caseLen, 12)).translate(0, 0, caseLen / 2), '#b48a3c'));
      parts.push(coloured(Z(new THREE.CylinderGeometry(r + 0.018, r + 0.018, 0.02, 12)).translate(0, 0, 0.01), '#9a7432'));
    }
  }
  const z = caseLen;
  if (kind === 'he') {
    // the explosive round: the body, its yellow band, the ogive, the fuse
    parts.push(coloured(Z(new THREE.CylinderGeometry(r, r * 0.92, 0.32, 12)).translate(0, 0, z + 0.16), '#55603e'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(r + 0.002, r + 0.002, 0.03, 12)).translate(0, 0, z + 0.28), '#c8a83a'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(r * 0.35, r, 0.16, 12)).translate(0, 0, z + 0.4), '#55603e'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(r * 0.15, r * 0.35, 0.05, 8)).translate(0, 0, z + 0.505), '#9a8a50'));
  } else if (era === 'A') {
    // the dart: a long rod, its fins at the base, its point; in the cartridge, its sabot's three petals round its front half
    parts.push(coloured(Z(new THREE.CylinderGeometry(0.014, 0.016, 0.62, 8)).translate(0, 0, z + 0.31), '#3c3f42'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(0.002, 0.014, 0.06, 8)).translate(0, 0, z + 0.65), '#5b5f62'));
    for (let k = 0; k < 4; k++) parts.push(coloured(new THREE.BoxGeometry(0.004, 0.06, 0.09).translate(0, 0.03, z + 0.06).rotateZ((k * Math.PI) / 2 + Math.PI / 4), '#2c2e30'));
    if (form === 'cartridge') for (let k = 0; k < 3; k++) parts.push(coloured(new THREE.BoxGeometry(0.05, 0.03, 0.22).translate(0, 0.035, z + 0.2).rotateZ((k * 2 * Math.PI) / 3), '#7a6d52'));
  } else {
    // the full-bore solid shot: black, its ballistic cap's red tip
    parts.push(coloured(Z(new THREE.CylinderGeometry(r, r, 0.22, 12)).translate(0, 0, z + 0.11), '#26282a'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(r * 0.3, r, 0.16, 12)).translate(0, 0, z + 0.3), '#26282a'));
    parts.push(coloured(Z(new THREE.CylinderGeometry(r * 0.05, r * 0.3, 0.05, 8)).translate(0, 0, z + 0.405), '#a23a2c'));
  }
  const mesh = new THREE.Mesh(mergeGeometries(parts, false)!, ROUND_MAT()); g.add(mesh);
  if (form === 'flight') {
    const tracer = new THREE.Mesh(new THREE.CircleGeometry(kind === 'solid' && era === 'A' ? 0.02 : 0.045, 10), new THREE.MeshBasicMaterial({ color: '#ff8a3a' }));
    tracer.rotation.y = Math.PI; tracer.position.z = -0.002; g.add(tracer);
  }
  return g;
}
/** The dart's sabot petals as they fall away from the dart (`k` 0 at the muzzle … 1 gone), in the dart's frame. */
export function buildSabot(): THREE.Group {
  const g = new THREE.Group();
  for (let k = 0; k < 3; k++) {
    const petal = new THREE.Group(); petal.rotation.z = (k * 2 * Math.PI) / 3; g.add(petal);
    const b = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.03, 0.22), mat('#7a6d52')); b.position.set(0, 0.035, 0.2); petal.add(b);
  }
  return g;
}
export function poseSabot(s: THREE.Group, k: number): void {
  s.children.forEach(petal => { const b = petal.children[0]; b.position.y = 0.035 + 0.6 * k; b.rotation.x = -1.2 * k; });
  s.visible = k < 1;
}

// ---- the damaged forms (mobility kills) ------------------------------------------------------------------------------------

/**
 * Mobility kills drawn: `track` (−1 the left, +1 the right) — that side's track thrown (its loop gone; the links lying
 * unrolled on the ground behind the sprocket) and two of its road wheels knocked off (lying beside); `engine` — the engine
 * deck's grille blown up and askew over a scorched deck, three places for its smoke (`smoke`). Before the bake.
 */
export function damageTank(t: TankModel, o: { track?: -1 | 1; engine?: boolean }, p: UnitPalette): void {
  const { m } = t;
  if (o.track) {
    const side = o.track, loop = m.anim.tracks.find(l => Math.sign(l.x) === side);
    if (loop) {
      loop.mesh.visible = false;
      m.anim.tracks = m.anim.tracks.filter(l => l !== loop);
      // the links unrolled behind it on the ground, a gentle bend
      const links: THREE.BufferGeometry[] = [];
      for (let i = 0; i < 18; i++) {
        const z = loop.zr - 0.1 - i * 0.25, x = loop.x + side * 0.04 * i * i / 18;
        links.push(new THREE.BoxGeometry(0.56, 0.06, 0.2).rotateY(side * 0.01 * i).translate(x, 0.03, z));
      }
      for (let i = 0; i < 5; i++) links.push(new THREE.BoxGeometry(0.56, 0.06, 0.2).translate(loop.x, 0.03, loop.zr + 0.4 + i * 0.25));   // (under the wheels, where it lay)
      const strip = new THREE.Mesh(mergeGeometries(links, false)!, mat(p.dark)); strip.name = 'thrown track'; m.full.add(strip);
    }
    // two road wheels off that side, lying flat beside the hull
    const road = m.anim.wheels.filter(w => Math.sign(w.position.x) === side && w.position.y < 0.4).slice(1, 4).filter((_, i) => i !== 1);
    road.forEach((w, i) => {
      m.anim.wheels = m.anim.wheels.filter(x => x !== w);
      w.position.set(w.position.x + side * (0.9 + 0.3 * i), 0.2, w.position.z + 0.4 * i);
      w.rotation.set(0, 0, Math.PI / 2 + side * 0.2);
    });
  }
  if (o.engine) {
    // the deck's grille (the hull's mesh over the rear deck) blown up and askew; the deck under it scorched
    for (const c of [...m.hull.children]) {
      const mesh = c as THREE.Mesh;
      if (mesh.isMesh && Math.abs(c.position.z + 2.35) < 0.02 && c.position.y > 1.4 && c.position.y < 1.45 && (mesh.geometry as THREE.BoxGeometry).parameters?.width === 1.6) {
        mesh.material = mat('#1a1614');   // (the dark under the slats: burnt)
      }
    }
    const deck = group(m.hull, 0, 1.47, -2.05);
    const lid = box(deck, 1.6, 0.05, 0.62, p.metal, 0, 0, -0.31); lid.name = 'blown grille';
    deck.rotation.set(-0.75, 0.25, 0.12);
    deck.position.y += 0.08;
    box(m.hull, 1.7, 0.012, 1.0, '#221d1a', 0, 1.428, -2.3).name = 'scorch';
    for (const x of [-0.45, 0, 0.45]) t.smoke.push(anchor(m.hull, x, 1.5, -2.35));
  }
}

// ---- the fit (lane F's hit shape and zones) --------------------------------------------------------------------------------

const r3 = (v: number) => Math.round(v * 1000) / 1000;
function boxOf(root: THREE.Object3D, objects: THREE.Object3D[], except: THREE.Object3D[] = []): THREE.Box3 {
  root.updateMatrixWorld(true);
  const skip = new Set<THREE.Object3D>(), b = new THREE.Box3(), v = new THREE.Vector3();
  for (const e of except) e.traverse(o => skip.add(o));
  for (const obj of objects) obj.traverse(o => {
    const mesh = o as THREE.Mesh;
    if (skip.has(o) || !mesh.isMesh || !mesh.visible || o.userData.thin || o.name === 'aerial' || !mesh.geometry?.attributes.position) return;
    if ((mesh as THREE.InstancedMesh).isInstancedMesh) {
      const im = mesh as THREE.InstancedMesh, mm = new THREE.Matrix4(), pos = mesh.geometry.attributes.position;
      for (let k = 0; k < im.count; k++) { im.getMatrixAt(k, mm); mm.premultiply(mesh.matrixWorld); for (let i = 0; i < pos.count; i++) b.expandByPoint(v.fromBufferAttribute(pos, i).applyMatrix4(mm)); }
      return;
    }
    const pos = mesh.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) b.expandByPoint(v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld));
  });
  if (b.min.y < 0) b.min.y = 0;
  return b;
}
const asBox = (b: THREE.Box3): HitPart => {
  const c = b.getCenter(new THREE.Vector3()), h = b.getSize(new THREE.Vector3()).multiplyScalar(0.5);
  return { shape: 'box', centre: [r3(c.x), r3(c.y), r3(c.z)], half: [r3(h.x), r3(h.y), r3(h.z)] };
};
/**
 * The tank's fit in people (a person 1), the gun laid level and the turret ahead: the hull (all but the turret: the running
 * gear with it, as the carrier's), the turret its own box (the barrel, the coax's and the pintle's barrels, the aerial and the
 * rack's uprights thin: no target), and for the zones (§5.25 (b)) the running gear's box a side and the engine's (the rear
 * deck, down to the hull's floor). For HITBOXES and the zones' table, lane F's; tests hold the numbers as the hitboxes are.
 */
export function fitTank(p: UnitPalette, o: { era?: TankEra; length?: TankGunLength; turret?: TankTurret; commander?: TankCommander } = {}): { hull: HitPart; turret: HitPart; runningGear: [HitPart, HitPart]; engine: HitPart; model: TankModel } {
  const t = buildTank(p, 1, o);
  poseTank(t, { yaw: 0, pitch: 0 });
  const { m } = t, root = m.root;
  root.updateMatrixWorld(true);
  const hull = boxOf(root, [m.full], [m.turret!]), turret = boxOf(root, [m.turret!]);
  const gear = ([-1, 1] as const).map(side => {
    const parts: THREE.Object3D[] = [...m.anim.tracks.filter(l => Math.sign(l.x) === side).map(l => l.mesh), ...m.anim.wheels.filter(w => Math.sign(w.position.x) === side)];
    return asBox(boxOf(root, parts));
  }) as [HitPart, HitPart];
  // the engine: the rear deck's span (the grille's), from the hull's floor over the gear to the deck
  const s = 1 / TANK_GUN.person, engine: HitPart = { shape: 'box', centre: [0, r3((0.45 + 1.43) / 2 * s), r3(-2.4 * s)], half: [r3(0.85 * s), r3((1.43 - 0.45) / 2 * s), r3(0.35 * s)] };
  return { hull: asBox(hull), turret: asBox(turret), runningGear: gear, engine, model: t };
}
